import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';

import {
  Header,
  HeaderItem,
  Sidebar,
  SidebarItem,
  SidebarPageWrapper,
  SidebarTabLabel,
  SidebarTab,
  SidebarTabs,
  SidebarFooter,
} from '@trueprodigy/components';

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, withStateHandlers } from 'recompose';
import ability, { defineRules } from '@config/permissions/ability';

import styled from 'styled-components';
import HeaderZendeskWidget from '@components/HeaderZendeskWidget/HeaderZendeskWidget';
import HeaderWrapper from '@components/HeaderWrapper';
import PropertyLookupModal from '@modules/appraisal/components/propertyLookupModal';
import CreateWorkItemModal from './createWorkItem';
import * as globalActions from '@actions/global';
import {
  isSidebarVisibleSelector,
  userRightsSelector,
  userStatusSelector,
} from '@selectors/global';
import {
  selectedQueueSelector,
  selectedQueueWorkItemSelector,
} from '../selectors/global';
import { resetActiveRecords } from '../actions/global';
import SidebarRdsNameWidget from '@components/Sidebar/SidebarRdsNameWidget';
import SidebarCaseSettingWidget from '@components/Sidebar/SidebarCaseSettingWidget';
import SidebarAccountSettingsWidget from '@components/Sidebar/SidebarAccountSettingsWidget';

import { QueueRoutes } from '../constants';

import versionIcon from '@assets/images/PA_Version.png';
import screenIcon from '@assets/images/PA_Print-Screen.png';
import appsIcon from '@assets/images/PA_Apps.png';
import queueLogo from '@assets/images/true-prodigy_queue.png';
import rocketIcon from '@components/Sidebar/assets/rocket-icon.png';
import generateIcon from '@modules/sketch/assets/generate-icon.png';

const PageWrapper = ({
  activeTab,
  activeQueue,
  activeQueueWorkItem,
  children,
  history,
  isSidebarVisible,
  location,
  toggleSidebar,
  userRights,
  closeTab,
  setActiveTab,
  userStatus,
}) => {
  const userRightsJS = userRights.toJS();
  const isReadOnly = userStatus.get('readOnly');
  const [workItemModal, setWorkItemModal] = useState(false);

  const parseHeaderTitle = ({ pathname }) => {
    if (
      activeQueue &&
      pathname ===
        QueueRoutes.QueueView.replace(':queueID', activeQueue.get('queueID'))
    )
      return `${activeQueue.get('queueName')}`;
    if (pathname.includes('queue/queueworkitemview')) return `QUEUE WORK ITEM`;
    return '';
  };

  useEffect(() => {
    if (userRights && userRights.toJS() && userRights.toJS().length) {
      const rules = defineRules(userRights.toJS());
      ability.update(rules);
    }
  }, [userRights]);

  useEffect(() => {
    const isiPad = navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints;
    if (isSidebarVisible && isiPad) {
      toggleSidebar();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (activeQueueWorkItem) {
      setActiveTab(null, 1);
    }
  }, [activeQueueWorkItem]);

  return (
    <div>
      <Helmet>
        <title>Queue</title>
      </Helmet>
      <Sidebar
        isSidebarVisible={isSidebarVisible}
        logo={queueLogo}
        sidebarFooter={
          <SidebarFooter>
            <SidebarRdsNameWidget />
            <SidebarCaseSettingWidget />
            <SidebarAccountSettingsWidget />
          </SidebarFooter>
        }
      >
        <SidebarItem icon={rocketIcon} link="/" title="Prodigy App Launcher" />
        <SidebarTabs
          indicatorColor="secondary"
          scrollButtons="off"
          textColor="primary"
          value={activeTab}
          variant="fullWidth"
          onChange={setActiveTab}
        >
          <SidebarTab
            label={
              <SidebarTabLabel isActive={activeTab === 0} label="APP MENU" />
            }
          />
          {activeQueueWorkItem && (
            <SidebarTab
              label={
                <SidebarTabLabel
                  hasClose
                  isActive={activeTab === 1}
                  label="Work Item"
                  labelStyle={{ fontSize: 12 }}
                  onClose={(event) => {
                    event.stopPropagation();

                    closeTab();
                    history.push(QueueRoutes.MyQueue);
                  }}
                />
              }
            />
          )}
        </SidebarTabs>
        {activeTab === 0 && (
          <>
            <SidebarItem link={QueueRoutes.QueueDashboard} title="Dashboard" />
            <SidebarItem link={QueueRoutes.MyQueue} title="My Queue" />
            <SidebarItem link={QueueRoutes.QueueSearch} title="Queue Search" />
            <SidebarItem
              link={QueueRoutes.WorkItemSearch}
              title="Work Item Search"
            />
            {activeQueue && (
              <SidebarItem
                link={QueueRoutes.QueueView.replace(
                  ':queueID',
                  activeQueue.get('queueID'),
                )}
                title="Queue View"
              />
            )}
            {userRightsJS.includes('QueueBatchImport') && !isReadOnly && (
              <SidebarItem nested title="App Functions">
                <SidebarItem
                  link={QueueRoutes.BatchImport}
                  title="Batch Import"
                />
                <SidebarItem
                  link={QueueRoutes.CopyAttachmentsToProperty}
                  title="Copy Attachments to Property"
                />
              </SidebarItem>
            )}
            {userRightsJS.includes('QueueAdministration') && !isReadOnly && (
              <SidebarItem nested title="Settings">
                <SidebarItem link={QueueRoutes.WorkTypes} title="Work Types" />
                <SidebarItem link={QueueRoutes.Queue} title="Queue" />
                <SidebarItem
                  link={QueueRoutes.CodefileMaintenance}
                  title="Codefile Maintenance"
                />
                <SidebarItem link={QueueRoutes.Calendar} title="Calendar" />
              </SidebarItem>
            )}
          </>
        )}
        {activeTab === 1 && activeQueueWorkItem && (
          <SidebarItem
            link={QueueRoutes.QueueWorkItemView.replace(
              ':workitemID',
              activeQueueWorkItem.get('workitemID'),
            )}
            title="Queue Work Item"
          />
        )}
      </Sidebar>
      <PageWrapper.ContentWrapper>
        <SidebarPageWrapper isSidebarVisible={isSidebarVisible}>
          <HeaderWrapper>
            <Header
              title={parseHeaderTitle(location, queueLogo)}
              toggleSidebar={toggleSidebar}
            >
              <HeaderItem
                icon={generateIcon}
                title="Create Work Item"
                onClick={() => setWorkItemModal(true)}
              />
              <HeaderItem
                icon={screenIcon}
                title="Print Screen"
                onClick={() => window.print()}
              />
              <HeaderItem
                icon={versionIcon}
                title="Smart List"
                onClick={() =>
                  history.push('/queue/smart-lists?module=Prodigy%20Queue')
                }
              />
              <HeaderItem icon={appsIcon} title="Apps" />
              <HeaderZendeskWidget />
            </Header>
          </HeaderWrapper>

          <div>{children}</div>
        </SidebarPageWrapper>
      </PageWrapper.ContentWrapper>
      <PropertyLookupModal />
      <CreateWorkItemModal
        open={workItemModal}
        handleClose={() => setWorkItemModal(false)}
      />
    </div>
  );
};

PageWrapper.propTypes = {
  activeQueue: PropTypes.any,
  activeQueueWorkItem: PropTypes.any,
  activeTab: PropTypes.number.isRequired,
  children: PropTypes.node,
  closeTab: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  isSidebarVisible: PropTypes.bool,
  location: PropTypes.object.isRequired,
  resetActiveRecords: PropTypes.func.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  toggleSidebar: PropTypes.func.isRequired,
  userRights: ImmutablePropTypes.list,
  userStatus: PropTypes.any,
};

PageWrapper.ContentWrapper = styled.div`
  > div {
    margin-top: -67px;
    min-height: 100vh;
    background-color: #f2f4f6;
  }
`;

const mapStateToProps = (state) => ({
  activeQueue: selectedQueueSelector(state),
  activeQueueWorkItem: selectedQueueWorkItemSelector(state),
  isSidebarVisible: isSidebarVisibleSelector(state),
  userRights: userRightsSelector(state),
  userStatus: userStatusSelector(state),
});

const mapDispatchToProps = {
  toggleSidebar: globalActions.toggleSidebar,
  resetActiveRecords,
};

const enhance = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(({ activeTab = 0 }) => ({ activeTab }), {
    setActiveTab: () => (_, activeTab) => ({ activeTab }),
    closeTab:
      (_, { resetActiveRecords }) =>
      () => {
        resetActiveRecords();
        return { activeTab: 0 };
      },
  }),
);

export default enhance(PageWrapper);
